/**
 * The canvas dock.
 *
 * Bottom-*left*, deliberately: the right corner is where a toast lands and
 * where the inspector's edge sits, and a dock there was competing with both.
 * Grid and Snap are worded rather than drawn — a magnet glyph and a grid glyph
 * side by side are two abstractions the planner has to decode every time, and
 * the words cost four characters each.
 *
 * The compact editor's dock leads with Select, which is Shift for a touch
 * screen: while it is on, taps pick tables and a drag on the floor draws a box.
 * "Done" hands the picked tables to the sheet.
 */

import { motion, useReducedMotion } from 'framer-motion'

import { tableBounds, unionBounds } from '../lib/geometry'
import type { Plan } from '../lib/types'
import { useT } from '../lib/i18n/I18nProvider'
import { usePlanStore } from '../store/planStore'
import { MAX_ZOOM, minZoomFor, useViewStore } from '../store/viewStore'

interface Props {
  plan: Plan
  stageRef: React.RefObject<HTMLDivElement>
}

export function ZoomControls({ plan, stageRef }: Props) {
  const t = useT()
  const reduceMotion = useReducedMotion()
  const zoom = useViewStore((s) => s.zoom)
  const atFloor = useViewStore((s) => s.zoom <= minZoomFor(s) + 0.001)
  const showGrid = useViewStore((s) => s.showGrid)
  const snapToGrid = useViewStore((s) => s.snapToGrid)
  const compact = useViewStore((s) => s.compact)
  const selectMode = useViewStore((s) => s.selectMode)
  const picked = usePlanStore((s) => s.selectedTableIds.length)

  const size = () => {
    const rect = stageRef.current?.getBoundingClientRect()
    return { width: rect?.width ?? 1200, height: rect?.height ?? 800 }
  }

  const step = (factor: number) => {
    const { width, height } = size()
    useViewStore.getState().zoomAt(factor, width / 2, height / 2)
  }

  const fit = () => {
    const bounds = unionBounds(
      plan.tables.map((table) =>
        tableBounds(table.shape, table.seatCount, table.x, table.y, table.rotation),
      ),
    )
    if (bounds) useViewStore.getState().fitTo(bounds, size())
    else useViewStore.getState().resetCamera({ x: size().width / 2, y: size().height / 2, zoom: 1 })
  }

  const reset = () => {
    const { width, height } = size()
    useViewStore.getState().zoomAt(1 / zoom, width / 2, height / 2)
  }

  const toggleSelect = () => {
    const view = useViewStore.getState()
    if (view.selectMode) {
      view.setSelectMode(false)
      if (usePlanStore.getState().selectedTableIds.length > 0) view.openPanel('selection')
      return
    }
    view.setSelectMode(true)
    view.closePanel()
    usePlanStore.getState().showToast(t.panels.selectHint, 'info')
  }

  return (
    <motion.div
      className="dock"
      initial={reduceMotion ? false : { opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.12, duration: 0.28, ease: [0.23, 1, 0.32, 1] }}
    >
      {compact && (
        <>
          <button
            type="button"
            className={`dock-toggle dock-select${selectMode ? ' is-on' : ''}`}
            onClick={toggleSelect}
            aria-pressed={selectMode}
          >
            {selectMode ? t.panels.selectDone(picked) : t.panels.select}
          </button>
          <span className="dock-rule" />
        </>
      )}

      <button
        type="button"
        className={`dock-toggle${showGrid ? ' is-on' : ''}`}
        onClick={() => useViewStore.getState().toggleGrid()}
        title={t.zoom.showGrid}
        aria-pressed={showGrid}
      >
        {t.zoom.grid}
      </button>

      <button
        type="button"
        className={`dock-toggle${snapToGrid ? ' is-on' : ''}`}
        onClick={() => useViewStore.getState().toggleSnap()}
        title={t.zoom.snapHint}
        aria-pressed={snapToGrid}
      >
        {t.zoom.snap}
      </button>

      <span className="dock-rule" />

      <button
        type="button"
        className="dock-btn"
        onClick={() => step(1 / 1.25)}
        disabled={atFloor}
        title={t.zoom.zoomOut}
        aria-label={t.zoom.zoomOut}
      >
        <svg viewBox="0 0 24 24" width="15" height="15" aria-hidden="true">
          <path d="M5 12h14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        </svg>
      </button>

      <button
        type="button"
        className="mono dock-zoom"
        onClick={reset}
        title={t.zoom.reset}
        aria-label={t.zoom.zoomLevel(Math.round(zoom * 100))}
      >
        {Math.round(zoom * 100)}%
      </button>

      <button
        type="button"
        className="dock-btn"
        onClick={() => step(1.25)}
        disabled={zoom >= MAX_ZOOM - 0.001}
        title={t.zoom.zoomIn}
        aria-label={t.zoom.zoomIn}
      >
        <svg viewBox="0 0 24 24" width="15" height="15" aria-hidden="true">
          <path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        </svg>
      </button>

      <span className="dock-rule" />

      <button type="button" className="dock-toggle" onClick={fit} title={t.zoom.fitPlan}>
        {t.zoom.fitRoom}
      </button>
    </motion.div>
  )
}
